import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { toReadablePrice, toReadableSquarePrice } from 'lib/utils';
import styles from './styles.module.scss';
import iconBuildingDefault from '../../assets/images/building-default.png';
import useDeleteFavoriteListFacilityItem from 'api/hooks/use-favorite-list-facility-item-delete';
import { EAvailabilityStatus, MarketTypes } from 'api/types';
import BottomSheetItem from 'components/bottom-sheet-item';
import FavoriteNoteForm from 'components/favorite-note-form';
import BottomSheet from 'components/bottom-sheet';
import FormConfirm from 'components/form-confirm';
import useFavoriteFacilityItemRevert from 'api/hooks/use-favorite-facility-item-revert';
import { Collapse } from 'react-collapse';
import useFavoriteNoteById from 'api/hooks/use-favorite-note-by-id';
import iconNoteHide from 'assets/images/icon-note-hide.svg';
import iconNote from 'assets/images/icon-note.svg';
import Header from 'components/header';

export type ApartmentListItemProps = {
  className?: string;
  housingEstate: string;
  image?: string;

  isFavorite?: boolean;
  notificationEnabled?: boolean;
  icon?: React.ReactNode;
  priceDirection?: 'up' | 'down';
  onClick?: () => void;
  favoriteId: number;
  facilityId: number;
  refetchList: () => void;

  apartmentComplexTitle: string;
  attachment: string;
  availabilityStatus: { key: EAvailabilityStatus; translation: string };
  facilityStatus: { key: EAvailabilityStatus; translation: string };
  buildingTitle: string;
  id: number;
  marketType: MarketTypes;
  price: number;
  pricePerSqM: number;
  title: string;
  rejectedByClient?: boolean;
  isClient: boolean;
  hasNote: boolean;
};

const FavoritesFacilityListItem: React.FC<ApartmentListItemProps> = (props) => {
  const {
    className,
    title,
    buildingTitle,
    apartmentComplexTitle,
    pricePerSqM,
    price,
    attachment,
    icon,
    onClick,
    favoriteId,
    facilityId,
    refetchList,
    availabilityStatus,
    facilityStatus,
    rejectedByClient,
    isClient,
    hasNote,
    marketType,
  } = props;
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isOpenCollapse, setIsOpenCollapse] = useState(false);
  const [isDisableFetchNote, setIsDisableFetchNote] = useState(true);

  const { data: dataNote, mutate } = useFavoriteNoteById(
    favoriteId,
    facilityId,
    isDisableFetchNote,
  );

  useEffect(() => {
    if (isDisableFetchNote) {
      mutate();
    }
  }, [isDisableFetchNote]);

  const handleCollapse = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    if (isDisableFetchNote) {
      return setIsDisableFetchNote(false);
    }
    setIsOpenCollapse((collapse) => !collapse);
  };

  useEffect(() => {
    if (dataNote) {
      setIsOpenCollapse((collapse) => !collapse);
    }
  }, [dataNote]);

  const { isMutating, trigger } = useDeleteFavoriteListFacilityItem(
    favoriteId,
    facilityId,
    isClient,
  );

  const { isMutating: isMutatingReturn, trigger: triggerReturn } = useFavoriteFacilityItemRevert(
    favoriteId,
    facilityId,
  );

  const handleDeleteFacilityItem = async () => {
    if (favoriteId && facilityId) {
      await trigger();
      setIsBottomSheetOpen(false);
      refetchList();
    }
  };

  const handleReturnObject = async () => {
    if (favoriteId && facilityId) {
      await triggerReturn();
      setIsBottomSheetOpen(false);
      refetchList();
    }
  };

  const isAvailable =
    facilityStatus.key === EAvailabilityStatus.Available &&
    availabilityStatus.key === EAvailabilityStatus.Available;

  const getStatusFacility = () => {
    return rejectedByClient ? 'отклонен' : isAvailable ? 'доступен' : 'не доступен';
  };

  const [showBottomSheetContent, setShowBottomSheetContent] = useState<
    'note' | 'delete' | 'return' | 'default'
  >('default');

  const handleDismiss = () => {
    setIsBottomSheetOpen(false);
    setShowBottomSheetContent('default');
  };

  const getViewBottomSheet = () => {
    if (showBottomSheetContent === 'note') {
      return (
        <FavoriteNoteForm
          facilityId={facilityId}
          favoriteListId={favoriteId}
          refetchList={refetchList}
          onDismiss={handleDismiss}
        />
      );
    }

    if (showBottomSheetContent === 'delete') {
      return (
        <FormConfirm
          onDismiss={handleDismiss}
          onAgree={() => handleDeleteFacilityItem()}
          title={`Вы уверены что хотите ${isClient ? 'отклонить' : 'удалить'} объект?`}
          isLoading={isMutating}
          nameAction={isClient ? 'отклонить' : 'удалить'}
          isSwitchActions
        />
      );
    }

    if (showBottomSheetContent === 'return') {
      return (
        <FormConfirm
          onDismiss={handleDismiss}
          onAgree={() => handleReturnObject()}
          title={`Вы уверены что хотите вернуть объект?`}
          isLoading={isMutatingReturn}
          nameAction={'вернуть'}
        />
      );
    }

    return (
      <div>
        {!isClient && (
          <BottomSheetItem
            title='Перейти в заметку'
            onClick={() => setShowBottomSheetContent('note')}
          />
        )}

        {!isClient && rejectedByClient && (
          <BottomSheetItem
            className={styles.colorRed}
            title='Вернуть объект'
            onClick={() => setShowBottomSheetContent('return')}
          />
        )}

        <BottomSheetItem
          className={styles.colorRed}
          title='Удалить'
          onClick={() => setShowBottomSheetContent('delete')}
          variant='danger'
        />
      </div>
    );
  };

  const getHeader = () => {
    if (showBottomSheetContent === 'note') {
      return <Header text='Заметка по объекту' />;
    }
    return undefined;
  };

  return (
    <div>
      <div
        className={clsx(
          styles.container,
          className,
          !isAvailable && styles.container_availableBad,
          rejectedByClient && styles.container_rejected,
        )}
        onClick={onClick}
      >
        <div className={styles.image}>
          {attachment && !!attachment ? (
            <img
              alt={'Изобра\nжение'}
              src={attachment && attachment}
              width={styles.imageSize}
              height={styles.imageSize}
            />
          ) : (
            <img alt='Изображение планировки ' src={iconBuildingDefault} />
          )}
        </div>
        <div className={styles.description}>
          <div className={styles.housingEstate}>
            {marketType === MarketTypes.PRIMARY ? apartmentComplexTitle : buildingTitle}
          </div>

          {price !== undefined && (
            <div className={styles.prices}>
              <span className={styles.price}>{toReadablePrice(price)}</span>
              {pricePerSqM !== undefined && (
                <span className={styles.squarePrice}>{toReadableSquarePrice(pricePerSqM)}</span>
              )}
            </div>
          )}
          <div className={styles.subTitle}>{title}</div>
          <div className={styles.subTitle}>{`Статус: ${getStatusFacility()}`}</div>
        </div>

        {!isClient && hasNote && (
          <div onClick={(e) => handleCollapse(e)} className={clsx(styles.icon, styles.leftIcon)}>
            <img src={isOpenCollapse ? iconNoteHide : iconNote} alt='note' height={22} />
          </div>
        )}

        {icon && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (isClient) {
                setIsBottomSheetOpen(true);
                return setShowBottomSheetContent('delete');
              }
              setIsBottomSheetOpen(true);
            }}
            className={clsx(styles.icon, !isClient && styles.icon_freeDots)}
          >
            {icon}
          </div>
        )}
        <BottomSheet
          header={getHeader()}
          open={isBottomSheetOpen}
          onDismiss={handleDismiss}
          snapPoints={({ minHeight, maxHeight }) => maxHeight - 100}
          blocking={true}
          onClick={(e) => e.stopPropagation()}
        >
          {getViewBottomSheet()}
        </BottomSheet>
      </div>

      {dataNote?.note && (
        <div className={styles.collapseContainer}>
          <Collapse isOpened={isOpenCollapse}>
            <div>{dataNote?.note}</div>
          </Collapse>
        </div>
      )}
    </div>
  );
};

export const APARTMENT_LIST_ITEM_HEIGHT = parseInt(styles.containerHeight);
export default FavoritesFacilityListItem;
